import { Anchor, Badge, Breadcrumbs, Card, Container, Grid, Group, Stack, Text, Title } from '@mantine/core'
import { createFileRoute, Link } from '@tanstack/react-router'
import { KindBadge } from '@/frontend/components/marina/KindBadge'
import { useBoat } from '@/frontend/hooks/useBoats'

const FT_PER_METER = 3.28084

export const Route = createFileRoute('/dashboard/accounts/boats/$id')({
  component: BoatDetail,
})

function BoatDetail() {
  const { id } = Route.useParams()
  const { data, isLoading } = useBoat(id)

  if (isLoading)
    return (
      <Container size="xl" py="md">
        Loading…
      </Container>
    )
  if (!data)
    return (
      <Container size="xl" py="md">
        Not found
      </Container>
    )
  const b = data.boat
  const customerName = b.customer ? `${b.customer.firstName} ${b.customer.lastName}`.trim() : '—'

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        <Breadcrumbs>
          <Anchor component={Link} to="/dashboard/accounts/boats" c="dimmed">
            Boats
          </Anchor>
          <span>{b.name}</span>
        </Breadcrumbs>

        <Card padding="lg" radius="md">
          <Group justify="space-between" align="flex-start">
            <div>
              <Group gap="xs" mb={4}>
                <Title order={3}>{b.name}</Title>
                <Badge variant="light" color="gray">
                  {formatType(b.type)}
                </Badge>
                <Badge variant="light" color={b.vesselType === 'COMMERCIAL' ? 'orange' : 'blue'}>
                  {b.vesselType}
                </Badge>
              </Group>
              <Text c="dimmed" size="sm">
                {customerName} {b.customer?.category && <KindBadge ml="xs" category={b.customer.category} />}
              </Text>
            </div>
            <Badge variant="light" color={b.location === 'Unknown' ? 'orange' : 'teal'} size="lg">
              {b.location || 'Unknown'}
            </Badge>
          </Group>
        </Card>

        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Card padding="lg" radius="md">
              <Text fw={600} mb="sm">
                Dimensions
              </Text>
              <Grid>
                <Info label="LOA" value={fmt(b.loaMeters)} />
                <Info label="Beam" value={fmt(b.beamMeters)} />
                <Info label="Draft" value={fmt(b.draftMeters)} />
                <Info label="Weight (tons)" value={b.weightTons} />
                <Info label="Fuel (L)" value={b.fuelCapacityL} />
                <Info label="Water (L)" value={b.waterCapacityL} />
              </Grid>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <Card padding="lg" radius="md">
              <Text fw={600} mb="sm">
                Registration
              </Text>
              <Grid>
                <Info label="Registration" value={b.registration} />
                <Info label="IMO Number" value={b.imoNumber} />
                <Info label="Hull ID" value={b.hullId} />
                <Info label="Flag State" value={b.flagState} />
                <Info label="Port of Registry" value={b.portOfRegistry} />
              </Grid>
            </Card>
          </Grid.Col>

          <Grid.Col span={12}>
            <Card padding="lg" radius="md">
              <Text fw={600} mb="sm">
                Identity
              </Text>
              <Grid>
                <Info label="Manufacturer" value={b.manufacturer} />
                <Info label="Model" value={b.model} />
                <Info label="Year Built" value={b.yearBuilt ? String(b.yearBuilt) : null} />
                <Info label="Color" value={b.color} />
              </Grid>
              {b.notes && (
                <>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={500} mt="md">
                    Notes
                  </Text>
                  <Text size="sm">{b.notes}</Text>
                </>
              )}
            </Card>
          </Grid.Col>
        </Grid>
      </Stack>
    </Container>
  )
}

function formatType(t: string): string {
  return t
    .split('_')
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(' ')
}

function fmt(meters: string | null | undefined): string {
  if (!meters) return '-'
  const m = Number(meters)
  if (!Number.isFinite(m) || m === 0) return '-'
  return `${(m * FT_PER_METER).toFixed(1)} ft (${m.toFixed(1)} m)`
}

function Info({ label, value }: { label: string; value: string | number | null | undefined }) {
  return (
    <Grid.Col span={{ base: 12, sm: 6 }}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
        {label}
      </Text>
      <Text size="sm">{value || '—'}</Text>
    </Grid.Col>
  )
}
